import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import logoMark from '@images/logo-mark.png';

/**
 * Главная визуальная композиция: рука держит прозрачный смартфон
 * с интерфейсом приложения SMART-KVADRAT (используется официальный логотип).
 *
 * Всё нарисовано векторно (SVG), поэтому изображение остаётся чётким
 * на любых экранах и не требует внешних файлов.
 * Чтобы заменить иллюстрацию на реальное фото: положите файл в
 * src/images/ и замените содержимое этого компонента на <StaticImage />.
 */
const StyledWrap = styled.div`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  margin: 0 auto;
  filter: drop-shadow(0 44px 80px rgba(86, 112, 246, 0.35));

  @media (prefers-reduced-motion: no-preference) {
    animation: phone-float 7s ease-in-out infinite;
  }

  @keyframes phone-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-14px);
    }
  }
`;

const HeroPhone = ({ maxWidth = 560 }) => (
  <StyledWrap $maxWidth={maxWidth}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="24 52 476 664"
      role="img"
      aria-label="Смартфон с приложением SMART-KVADRAT в руке"
      style={{ display: 'block', width: '100%', height: 'auto' }}>
      <title>Смартфон с приложением SMART-KVADRAT в руке</title>
      <defs>
        <linearGradient id="hp-skin" x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0" stopColor="#5b6ea6" />
          <stop offset="0.45" stopColor="#2b3760" />
          <stop offset="1" stopColor="#141c34" />
        </linearGradient>
        <linearGradient id="hp-finger" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#4a5a92" />
          <stop offset="0.5" stopColor="#26325a" />
          <stop offset="1" stopColor="#151d36" />
        </linearGradient>
        <linearGradient id="hp-screen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0e1730" />
          <stop offset="0.6" stopColor="#0a1122" />
          <stop offset="1" stopColor="#121c38" />
        </linearGradient>
        <linearGradient id="hp-accent" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#5670F6" />
          <stop offset="0.55" stopColor="#7C5CFF" />
          <stop offset="1" stopColor="#FD76FF" />
        </linearGradient>
        <linearGradient id="hp-glass" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.28" />
          <stop offset="0.4" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Рука за смартфоном: ладонь, запястье, четыре пальца */}
      <g fill="url(#hp-skin)">
        <rect x="60" y="560" width="180" height="160" rx="56" />
        <rect x="36" y="392" width="196" height="230" rx="74" />
        <g fill="url(#hp-finger)">
          <rect x="72" y="176" width="50" height="320" rx="25" />
          <rect x="122" y="146" width="50" height="352" rx="25" />
          <rect x="172" y="176" width="50" height="332" rx="25" />
          <rect x="220" y="248" width="46" height="270" rx="23" />
        </g>
      </g>

      {/* Смартфон */}
      <rect
        x="232"
        y="62"
        width="252"
        height="536"
        rx="48"
        fill="url(#hp-screen)"
        stroke="#ffffff"
        strokeOpacity="0.5"
        strokeWidth="2"
      />
      <rect x="232" y="62" width="252" height="536" rx="48" fill="url(#hp-glass)" />
      <rect
        x="246"
        y="76"
        width="224"
        height="508"
        rx="36"
        fill="url(#hp-screen)"
        stroke="#ffffff"
        strokeOpacity="0.08"
      />

      <rect x="316" y="86" width="84" height="18" rx="9" fill="#04060d" />
      <text x="268" y="116" fontFamily="Arial, Helvetica, sans-serif" fontSize="13" fontWeight="700" fill="#dbe4ff">
        9:41
      </text>
      <rect x="394" y="106" width="56" height="12" rx="6" fill="#ffffff" fillOpacity="0.18" />

      <image x="266" y="134" width="26" height="26" href={logoMark} />
      <text x="302" y="153" fontFamily="Arial, Helvetica, sans-serif" fontSize="14" fontWeight="700" fill="#ffffff">
        SMART-KVADRAT
      </text>
      <line x1="268" y1="176" x2="448" y2="176" stroke="#ffffff" strokeOpacity="0.1" />

      <text x="268" y="222" fontFamily="Arial, Helvetica, sans-serif" fontSize="23" fontWeight="700" fill="#ffffff">
        Разработка
      </text>
      <text x="268" y="252" fontFamily="Arial, Helvetica, sans-serif" fontSize="23" fontWeight="700" fill="url(#hp-accent)">
        программного
      </text>
      <text x="268" y="282" fontFamily="Arial, Helvetica, sans-serif" fontSize="23" fontWeight="700" fill="#ffffff">
        обеспечения
      </text>

      <rect x="268" y="302" width="150" height="8" rx="4" fill="#ffffff" fillOpacity="0.22" />
      <rect x="268" y="318" width="106" height="8" rx="4" fill="#ffffff" fillOpacity="0.14" />

      <rect x="268" y="346" width="132" height="40" rx="20" fill="url(#hp-accent)" />
      <text
        x="334"
        y="371"
        textAnchor="middle"
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="13"
        fontWeight="700"
        fill="#ffffff">
        Подробнее
      </text>

      <rect
        x="268"
        y="410"
        width="180"
        height="132"
        rx="18"
        fill="#ffffff"
        fillOpacity="0.06"
        stroke="#ffffff"
        strokeOpacity="0.12"
      />
      <text x="284" y="436" fontFamily="Arial, Helvetica, sans-serif" fontSize="11" fontWeight="700" fill="#aebcff">
        AI · IT-проект
      </text>
      <rect x="284" y="494" width="16" height="28" rx="5" fill="url(#hp-accent)" fillOpacity="0.85" />
      <rect x="306" y="480" width="16" height="42" rx="5" fill="url(#hp-accent)" fillOpacity="0.85" />
      <rect x="328" y="462" width="16" height="60" rx="5" fill="url(#hp-accent)" fillOpacity="0.9" />
      <rect x="350" y="446" width="16" height="76" rx="5" fill="url(#hp-accent)" />
      <rect x="372" y="466" width="16" height="56" rx="5" fill="url(#hp-accent)" fillOpacity="0.9" />
      <rect x="394" y="434" width="16" height="88" rx="5" fill="url(#hp-accent)" />
      <rect x="284" y="452" width="80" height="6" rx="3" fill="#ffffff" fillOpacity="0.16" />

      <path d="M 264 88 L 306 74 L 280 576 L 256 570 Z" fill="#ffffff" fillOpacity="0.05" />

      {/* Большой палец на переднем плане */}
      <rect
        x="150"
        y="500"
        width="250"
        height="84"
        rx="42"
        transform="rotate(32 275 542)"
        fill="url(#hp-finger)"
        stroke="#7f9bff"
        strokeOpacity="0.22"
      />

      <g stroke="#8aa2ff" strokeOpacity="0.18" strokeWidth="2" fill="none">
        <path d="M 122 190 L 122 470" />
        <path d="M 172 190 L 172 468" />
        <path d="M 220 262 L 220 470" />
      </g>
    </svg>
  </StyledWrap>
);

HeroPhone.propTypes = {
  maxWidth: PropTypes.number,
};

export default HeroPhone;
